import { apiService } from './api';

export interface NotificationItem {
  id: number;
  notification_config_id: number;
  url_id: number | null;
  trigger_type: 'failure' | 'recovery' | 'test';
  status: 'success' | 'failed';
  message: string;
  error_message: string;
  sent_at: string;
  notification_config: {
    id: number;
    name: string;
    type: string;
  };
  url: {
    id: number;
    name: string;
    url: string;
  } | null;
  read_at?: string | null; // 用于前端标记已读状态
}

export interface NotificationStats {
  total_sent: number;
  success_rate: number;
  recent_failures: number;
  unread_count: number;
}

class NotificationService {
  private baseURL = '/api/v1';

  // 缓存最近的通知数据，避免重复请求
  private recentNotificationsCache: { data: NotificationItem[]; timestamp: number } | null = null;
  // 缓存通知日志查询结果，避免重复API调用
  private notificationLogsCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 30 * 1000; // 30秒缓存

  // 获取未读通知数量（已弃用，使用getNotificationStats代替）
  async getUnreadCount(): Promise<number> {
    try {
      const stats = await this.getNotificationStats();
      return stats.unread_count;
    } catch (error) {
      console.error('获取未读通知数量失败:', error);
      return 0;
    }
  }

  // 获取通知列表（支持完整筛选参数和缓存）
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    notification_config_id?: number;
    url_id?: number;
    trigger_type?: string;
    status?: string;
    start_time?: string;
    end_time?: string;
    search?: string;
  }): Promise<{
    data: NotificationItem[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      // 构建查询参数
      const queryParams = new URLSearchParams();
      const page = params?.page || 1;
      const limit = params?.limit || 20;
      
      queryParams.set('page', page.toString());
      queryParams.set('limit', limit.toString());
      
      if (params?.search) queryParams.set('search', params.search);
      if (params?.notification_config_id) queryParams.set('notification_config_id', params.notification_config_id.toString());
      if (params?.url_id) queryParams.set('url_id', params.url_id.toString());
      if (params?.trigger_type) queryParams.set('trigger_type', params.trigger_type);
      if (params?.status) queryParams.set('status', params.status);
      if (params?.start_time) queryParams.set('start_time', params.start_time);
      if (params?.end_time) queryParams.set('end_time', params.end_time);
      
      const queryString = queryParams.toString();
      
      // 检查缓存（避免30秒内重复请求）
      const cacheKey = queryString;
      const cachedData = this.notificationLogsCache.get(cacheKey);
      const now = Date.now();
      
      if (cachedData && (now - cachedData.timestamp) < this.CACHE_DURATION) {
        return cachedData.data;
      }
      
      const response = await apiService.get(`${this.baseURL}/notification-logs?${queryString}`);
      
      const result = {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || page,
        limit: response.limit || limit,
      };
      
      // 缓存结果
      this.notificationLogsCache.set(cacheKey, {
        data: result,
        timestamp: now
      });
      
      // 清理过期缓存
      this.cleanupCache();
      
      return result;
    } catch (error) {
      console.error('获取通知列表失败:', error);
      return {
        data: [],
        total: 0,
        page: params?.page || 1,
        limit: params?.limit || 20,
      };
    }
  }

  // 获取最近的通知（用于Header下拉显示）
  async getRecentNotifications(limit = 10): Promise<NotificationItem[]> {
    try {
      // 检查缓存是否有效
      const now = Date.now();
      if (this.recentNotificationsCache && 
          (now - this.recentNotificationsCache.timestamp) < this.CACHE_DURATION &&
          this.recentNotificationsCache.data.length >= limit) {
        return this.recentNotificationsCache.data.slice(0, limit);
      }

      const response = await this.getNotifications({ page: 1, limit: Math.max(limit, 50) });
      
      // 更新缓存
      this.recentNotificationsCache = {
        data: response.data,
        timestamp: now
      };
      
      return response.data.slice(0, limit);
    } catch (error) {
      console.error('获取最近通知失败:', error);
      return [];
    }
  }

  // 标记通知为已读（前端本地存储）
  markAsRead(notificationIds: number[]): void {
    try {
      const readNotifications = this.getReadNotifications();
      const newReadNotifications = Array.from(new Set([...readNotifications, ...notificationIds]));
      localStorage.setItem('read_notifications', JSON.stringify(newReadNotifications));
      
      // 清理缓存，确保下次获取时重新计算未读数量
      this.clearCache();
    } catch (error) {
      console.error('标记通知已读失败:', error);
    }
  }

  // 标记所有通知为已读
  markAllAsRead(): void {
    try {
      // 使用缓存的通知数据，避免额外的API调用
      if (this.recentNotificationsCache) {
        const allIds = this.recentNotificationsCache.data.map(n => n.id);
        this.markAsRead(allIds);
      } else {
        // 如果没有缓存，获取最近通知
        this.getRecentNotifications(100).then(notifications => {
          const allIds = notifications.map(n => n.id);
          this.markAsRead(allIds);
        });
      }
    } catch (error) {
      console.error('标记所有通知已读失败:', error);
    }
  }

  // 获取已读通知ID列表
  private getReadNotifications(): number[] {
    try {
      const stored = localStorage.getItem('read_notifications');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('获取已读通知列表失败:', error);
      return [];
    }
  }

  // 检查通知是否已读
  isNotificationRead(notificationId: number): boolean {
    const readNotifications = this.getReadNotifications();
    return readNotifications.includes(notificationId);
  }

  // 清理过期的已读记录（保留最近30天）
  cleanupReadNotifications(): void {
    try {
      // 这里可以根据实际需求实现清理逻辑
      // 例如：定期清理超过30天的已读记录
      const readNotifications = this.getReadNotifications();
      if (readNotifications.length > 1000) {
        // 保留最近的1000条记录
        const recent = readNotifications.slice(-1000);
        localStorage.setItem('read_notifications', JSON.stringify(recent));
      }
    } catch (error) {
      console.error('清理已读通知记录失败:', error);
    }
  }

  // 清理缓存
  private clearCache(): void {
    this.recentNotificationsCache = null;
    this.notificationLogsCache.clear();
  }
  
  // 清理过期缓存
  private cleanupCache(): void {
    const now = Date.now();
    for (const [key, value] of this.notificationLogsCache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        this.notificationLogsCache.delete(key);
      }
    }
  }

  // 获取通知统计信息
  async getNotificationStats(): Promise<NotificationStats> {
    try {
      // 并行获取统计数据和最近通知
      const [statsResponse, recentNotifications] = await Promise.all([
        apiService.get(`${this.baseURL}/notification-stats`).catch(() => null),
        this.getRecentNotifications(50)
      ]);
      
      // 计算未读数量：基于最近1小时的通知
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const unreadCount = recentNotifications.filter((notification: NotificationItem) => {
        const sentAt = new Date(notification.sent_at);
        const isRecent = sentAt > oneHourAgo;
        const isUnread = !this.isNotificationRead(notification.id);
        return isRecent && isUnread;
      }).length;
      
      return {
        total_sent: statsResponse?.total_sent || 0,
        success_rate: statsResponse?.success_rate || 0,
        recent_failures: statsResponse?.failed_count || 0,
        unread_count: unreadCount,
      };
    } catch (error) {
      console.error('获取通知统计失败:', error);
      return {
        total_sent: 0,
        success_rate: 0,
        recent_failures: 0,
        unread_count: 0,
      };
    }
  }

  // 删除通知（如果后端支持）
  async deleteNotifications(notificationIds: number[]): Promise<void> {
    try {
      // 这里可以根据实际后端API实现删除功能
      // await axios.delete(`${this.baseURL}/notification-logs`, {
      //   data: { ids: notificationIds }
      // });
      
      // 暂时只从本地已读列表中移除
      const readNotifications = this.getReadNotifications();
      const filtered = readNotifications.filter(id => !notificationIds.includes(id));
      localStorage.setItem('read_notifications', JSON.stringify(filtered));
    } catch (error) {
      console.error('删除通知失败:', error);
      throw error;
    }
  }

  // 测试通知配置
  async testNotificationConfig(configId: number): Promise<void> {
    try {
      await apiService.post(`${this.baseURL}/notifications/${configId}/test`);
    } catch (error) {
      console.error('测试通知配置失败:', error);
      throw error;
    }
  }
}

export default new NotificationService();